import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Users, FileText, DollarSign, TrendingUp, Shield, Trash2,
  BarChart3, CreditCard, Activity, Settings, Coins, Save, Loader2,
  Linkedin, Facebook, Instagram, ImagePlus, PenLine, Key, Share2, Zap
} from "lucide-react";

const actionIcons = {
  article_blog: PenLine,
  post_linkedin: Linkedin,
  post_facebook: Facebook,
  post_instagram: Instagram,
  image_ai: ImagePlus,
};

const actionLabels = {
  article_blog: "Articulo Blog",
  post_linkedin: "Post LinkedIn",
  post_facebook: "Post Facebook",
  post_instagram: "Post Instagram",
  image_ai: "Imagen IA",
};

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  // Credit costs state
  const [creditCosts, setCreditCosts] = useState({});
  const [editCosts, setEditCosts] = useState({});
  const [savingCosts, setSavingCosts] = useState(false);

  // Plans state
  const [plans, setPlans] = useState({});
  const [editPlans, setEditPlans] = useState({});
  const [savingPlans, setSavingPlans] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);

  // Credit packs state
  const [editPacks, setEditPacks] = useState([]);
  const [savingPacks, setSavingPacks] = useState(false);
  const [initialCredits, setInitialCredits] = useState(5);
  const [savingInitialCredits, setSavingInitialCredits] = useState(false);

  // Tier config state
  const [tierConfig, setTierConfig] = useState({
    voice_profile: ["pro", "business"],
    editorial_angle: ["pro", "business"],
    content_objective: ["pro", "business"],
    multi_language: ["pro", "business"],
  });
  const [savingTiers, setSavingTiers] = useState(false);

  // Distribution (external social distribution)
  const [distributionUrl, setDistributionUrl] = useState("");
  const [distributionConfigured, setDistributionConfigured] = useState(false);
  const [savingDistribution, setSavingDistribution] = useState(false);
  const [testingDistribution, setTestingDistribution] = useState(false);

  // Pipedream workflows (per-network URLs)
  const [pdWorkflows, setPdWorkflows] = useState({ linkedin: "", facebook: "", instagram: "", x: "" });
  const [savingPdWorkflows, setSavingPdWorkflows] = useState(false);

  // Stripe sync
  const [syncingStripe, setSyncingStripe] = useState(false);
  const [stripeSyncResult, setStripeSyncResult] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    // Each endpoint in its own try/catch so ONE failure doesn't break the whole panel.
    const safe = async (fn) => { try { return await fn(); } catch (e) { console.warn("[admin] load failed:", e?.message); return null; } };
    try {
      const [s, u, t, cc, plansRes] = await Promise.all([
        safe(() => api.get("/admin/stats")),
        safe(() => api.get("/admin/users")),
        safe(() => api.get("/admin/transactions")),
        safe(() => api.get("/admin/credit-costs")),
        safe(() => api.get("/plans")),
      ]);
      if (s) setStats(s.data);
      if (u) setUsers(u.data);
      if (t) setTransactions(t.data);
      if (cc) { setCreditCosts(cc.data); setEditCosts(cc.data); }
      if (plansRes) {
        const plansObj = {};
        plansRes.data.forEach((p) => {
          plansObj[p.id] = {
            name: p.name,
            price: p.price,
            credits: p.credits,
            currency: p.currency || "eur",
            includes: p.includes || "",
            features: Array.isArray(p.features) ? p.features : [],
            offer_enabled: !!p.offer_enabled,
            offer_price: p.offer_price || 0,
            offer_text: p.offer_text || "",
          };
        });
        setPlans(plansObj);
        setEditPlans(plansObj);
      }
      // Non-critical extras (each isolated)
      await Promise.all([
        safe(async () => { const r = await api.get("/admin/stripe-status"); setStripeStatus(r.data); }),
        safe(async () => { const r = await api.get("/admin/tier-config"); setTierConfig(r.data); }),
        safe(async () => { const r = await api.get("/admin/credit-packs"); setEditPacks(r.data); }),
        safe(async () => { const r = await api.get("/admin/initial-credits"); setInitialCredits(r.data.credits); }),
        safe(async () => {
          const r = await api.get("/admin/distribution");
          setDistributionUrl(r.data.url || "");
          setDistributionConfigured(!!r.data.configured);
        }),
        safe(async () => {
          const r = await api.get("/admin/pipedream-workflows");
          setPdWorkflows({
            linkedin: r.data.workflows?.linkedin || "",
            facebook: r.data.workflows?.facebook || "",
            instagram: r.data.workflows?.instagram || "",
            x: r.data.workflows?.x || "",
          });
        }),
      ]);
      await loadStripeStatus();
    } catch (e) {
      console.error("[admin] unexpected:", e);
      toast.error("Error cargando datos admin");
    } finally {
      setLoading(false);
    }
  };

  const toggleAdmin = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/toggle-admin`);
      loadAll();
      toast.success("Rol actualizado");
    } catch { toast.error("Error"); }
  };

  const addCredits = async (userId) => {
    const amount = prompt("Creditos a anadir (numero positivo o negativo):");
    if (!amount || isNaN(parseInt(amount))) return;
    try {
      const res = await api.put(`/admin/users/${userId}/add-credits`, { credits: parseInt(amount) });
      loadAll();
      toast.success(`${amount} creditos ${parseInt(amount) > 0 ? "anadidos" : "eliminados"}. Nuevo balance: ${res.data.new_balance}`);
    } catch { toast.error("Error"); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Eliminar usuario y todos sus articulos?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      loadAll();
      toast.success("Usuario eliminado");
    } catch { toast.error("Error"); }
  };

  const saveCreditCosts = async () => {
    setSavingCosts(true);
    try {
      await api.put("/admin/credit-costs", { costs: editCosts });
      setCreditCosts(editCosts);
      toast.success("Costos de creditos actualizados");
    } catch { toast.error("Error al guardar"); }
    finally { setSavingCosts(false); }
  };

  const savePlans = async () => {
    setSavingPlans(true);
    try {
      await api.put("/admin/plans", { plans: editPlans });
      setPlans(editPlans);
      toast.success("Planes actualizados");
    } catch { toast.error("Error al guardar"); }
    finally { setSavingPlans(false); }
  };

  const savePacks = async () => {
    setSavingPacks(true);
    try {
      await api.put("/admin/credit-packs", editPacks);
      toast.success("Packs de creditos actualizados");
    } catch { toast.error("Error al guardar packs"); }
    finally { setSavingPacks(false); }
  };

  const updatePack = (index, field, value) => {
    setEditPacks((prev) => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const saveInitialCredits = async () => {
    setSavingInitialCredits(true);
    try {
      await api.put("/admin/initial-credits", { credits: initialCredits });
      toast.success(`Creditos iniciales: ${initialCredits}`);
    } catch { toast.error("Error"); }
    finally { setSavingInitialCredits(false); }
  };

  const saveTierConfig = async () => {
    setSavingTiers(true);
    try {
      await api.put("/admin/tier-config", tierConfig);
      toast.success("Configuracion de tiers actualizada");
    } catch { toast.error("Error al guardar"); }
    finally { setSavingTiers(false); }
  };

  const toggleTierPlan = (feature, plan) => {
    setTierConfig((prev) => {
      const current = prev[feature] || [];
      if (current.includes(plan)) {
        return { ...prev, [feature]: current.filter((p) => p !== plan) };
      }
      return { ...prev, [feature]: [...current, plan] };
    });
  };

  const saveDistribution = async () => {
    setSavingDistribution(true);
    try {
      const res = await api.put("/admin/distribution", { url: distributionUrl.trim() });
      setDistributionConfigured(!!res.data.configured);
      toast.success("Distribucion actualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingDistribution(false);
    }
  };

  const testDistribution = async () => {
    setTestingDistribution(true);
    try {
      const res = await api.post("/admin/distribution/test");
      if (res.data.ok) {
        toast.success(`Conexion OK (HTTP ${res.data.status})`);
      } else {
        toast.warning(`El destino respondio HTTP ${res.data.status}. Revisa que el escenario este activo.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al probar conexion");
    } finally {
      setTestingDistribution(false);
    }
  };

  const savePdWorkflows = async () => {
    setSavingPdWorkflows(true);
    try {
      const payload = {
        linkedin: (pdWorkflows.linkedin || "").trim(),
        facebook: (pdWorkflows.facebook || "").trim(),
        instagram: (pdWorkflows.instagram || "").trim(),
        x: (pdWorkflows.x || "").trim(),
      };
      await api.put("/admin/pipedream-workflows", payload);
      toast.success("Workflows guardados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingPdWorkflows(false);
    }
  };

  const loadStripeStatus = async () => {
    try {
      const res = await api.get("/admin/stripe-status");
      setStripeStatus(res.data);
    } catch {}
  };

  const syncStripeProducts = async () => {
    if (!window.confirm("Se crearán/actualizarán productos y precios en Stripe. ¿Continuar?")) return;
    setSyncingStripe(true);
    try {
      const res = await api.post("/admin/stripe/sync-products");
      setStripeSyncResult(res.data);
      toast.success("Stripe sincronizado correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar");
    } finally {
      setSyncingStripe(false);
    }
  };

  const tabs = [
    { id: "overview", label: "Resumen", icon: BarChart3 },
    { id: "users", label: "Usuarios", icon: Users },
    { id: "transactions", label: "Pagos", icon: CreditCard },
    { id: "pricing", label: "Creditos y Planes", icon: Coins },
    { id: "tiers", label: "Tiers", icon: Shield },
    { id: "settings", label: "Configuracion", icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: "#0B1622" }}>
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-12 text-center" style={{ color: "#94A3B8" }}>Cargando panel admin...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen" style={{ background: "#0B1622" }}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,212,255,0.1)" }}>
            <Shield className="w-5 h-5" style={{ color: "#00D4FF" }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#E2E8F0" }}>Panel de Administracion</h1>
            <p className="text-sm" style={{ color: "#94A3B8" }}>Gestiona usuarios, costos, planes e ingresos</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 p-1 rounded-lg overflow-x-auto" style={{ background: "#0F2035" }}>
          {tabs.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setActiveTab(t.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm transition-all duration-200 whitespace-nowrap"
              style={{ background: activeTab === t.id ? "rgba(0,212,255,0.1)" : "transparent",
                color: activeTab === t.id ? "#00D4FF" : "#94A3B8", fontFamily: "'Outfit', sans-serif" }}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {activeTab === "overview" && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Usuarios", value: stats.total_users, icon: Users, color: "#00D4FF" },
                { label: "Articulos", value: stats.total_articles, icon: FileText, color: "#4ADE80" },
                { label: "Costo IA", value: `$${stats.total_cost?.toFixed(2)}`, icon: Activity, color: "#F0A050" },
                { label: "Ingresos", value: `$${stats.total_revenue?.toFixed(2)}`, icon: DollarSign, color: "#A78BFA" },
              ].map((s) => (
                <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase()}`}
                  className="rounded-xl p-5 border animate-fade-in-up" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <s.icon className="w-4 h-4" style={{ color: s.color }} />
                    <span className="text-xs uppercase tracking-wider" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>{s.label}</span>
                  </div>
                  <div className="text-3xl font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk', sans-serif" }}>{s.value}</div>
                </div>
              ))}
            </div>

            <div className="rounded-xl p-6 border" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3 className="text-sm font-medium mb-4 flex items-center gap-2" style={{ color: "#E2E8F0" }}>
                <TrendingUp className="w-4 h-4" style={{ color: "#4ADE80" }} />Resumen Financiero
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="text-xs block mb-1" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>INGRESOS</span>
                  <span className="text-xl font-bold" style={{ color: "#4ADE80" }}>${stats.total_revenue?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-xs block mb-1" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>COSTOS IA</span>
                  <span className="text-xl font-bold" style={{ color: "#F0A050" }}>${stats.total_cost?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-xs block mb-1" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>MARGEN</span>
                  <span className="text-xl font-bold" style={{ color: "#A78BFA" }}>
                    ${(stats.total_revenue - stats.total_cost)?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-xl p-6 border" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3 className="text-sm font-medium mb-4" style={{ color: "#E2E8F0" }}>Distribucion de Planes</h3>
              <div className="flex gap-4">
                {Object.entries(stats.plan_distribution || {}).map(([plan, count]) => (
                  <div key={plan} className="flex-1 rounded-lg p-3 text-center" style={{ background: "#0B1622" }}>
                    <div className="text-lg font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{count}</div>
                    <div className="text-xs capitalize" style={{ color: "#94A3B8" }}>{plan}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users */}
        {activeTab === "users" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button data-testid="export-users-btn" size="sm" variant="outline"
                className="rounded-full text-xs"
                style={{ borderColor: "#1E3A5F", color: "#00D4FF" }}
                onClick={async () => {
                  try {
                    const res = await api.get("/admin/users/export");
                    const blob = new Blob([res.data.csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = "usuarios_laforja.csv"; a.click();
                    URL.revokeObjectURL(url);
                    toast.success(`${res.data.count} usuarios exportados`);
                  } catch { toast.error("Error al exportar"); }
                }}>
                Exportar CSV
              </Button>
            </div>
          <div className="rounded-xl border overflow-hidden" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid #1E3A5F" }}>
                    {["Nombre", "Email", "Plan", "Creditos", "Articulos", "Costo IA", "Fuente", "Admin", "Acciones"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="transition-colors duration-150" style={{ borderBottom: "1px solid #1E3A5F" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,212,255,0.03)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                      <td className="px-4 py-3 font-medium" style={{ color: "#E2E8F0" }}>{u.name}</td>
                      <td className="px-4 py-3" style={{ color: "#94A3B8" }}>{u.email}</td>
                      <td className="px-4 py-3">
                        <Badge className="text-xs rounded-full capitalize" style={{
                          background: u.plan === "business" ? "rgba(167,139,250,0.1)" : u.plan === "pro" ? "rgba(0,212,255,0.1)" : "rgba(148,163,184,0.1)",
                          color: u.plan === "business" ? "#A78BFA" : u.plan === "pro" ? "#00D4FF" : "#94A3B8" }}>
                          {u.plan || "free"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" style={{ fontFamily: "'IBM Plex Mono'", color: "#00D4FF" }}>{u.credits || 0}</td>
                      <td className="px-4 py-3" style={{ fontFamily: "'IBM Plex Mono'", color: "#E2E8F0" }}>{u.articles_used || 0}</td>
                      <td className="px-4 py-3" style={{ fontFamily: "'IBM Plex Mono'", color: "#F0A050" }}>${(u.total_cost || 0).toFixed(2)}</td>
                      <td className="px-4 py-3" style={{ fontFamily: "'IBM Plex Mono'", color: "#94A3B8", fontSize: "11px" }} title={u.signup_source || ""}>
                        {u.signup_source ? (
                          <Badge className="text-[10px] rounded-full" style={{ background: "rgba(74,222,128,0.1)", color: "#4ADE80" }}>
                            {u.signup_source.length > 18 ? u.signup_source.slice(0, 15) + "…" : u.signup_source}
                          </Badge>
                        ) : (
                          <span style={{ color: "#475569" }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button data-testid={`toggle-admin-${u.id}`} onClick={() => toggleAdmin(u.id)} className="text-xs px-2 py-1 rounded"
                          style={{ background: u.is_admin ? "rgba(0,212,255,0.1)" : "rgba(148,163,184,0.1)", color: u.is_admin ? "#00D4FF" : "#94A3B8" }}>
                          {u.is_admin ? "Admin" : "Usuario"}
                        </button>
                      </td>
                      <td className="px-4 py-3 flex gap-1">
                        <button data-testid={`add-credits-${u.id}`} onClick={() => addCredits(u.id)}
                          className="p-1.5 rounded transition-colors" style={{ color: "#4ADE80" }} title="Anadir creditos">
                          <Coins className="w-4 h-4" />
                        </button>
                        <button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u.id)}
                          className="p-1.5 rounded transition-colors" style={{ color: "#ef4444" }} title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

        {/* Transactions */}
        {activeTab === "transactions" && (
          <div className="rounded-xl border overflow-hidden" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
            {transactions.length === 0 ? (
              <div className="p-12 text-center" style={{ color: "#94A3B8" }}>No hay transacciones todavia</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid #1E3A5F" }}>
                      {["Fecha", "Usuario", "Tipo", "Monto", "Creditos", "Estado"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                          style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} style={{ borderBottom: "1px solid #1E3A5F" }}>
                        <td className="px-4 py-3" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>
                          {new Date(tx.created_at).toLocaleDateString("es-ES")}
                        </td>
                        <td className="px-4 py-3" style={{ color: "#E2E8F0" }}>
                          {tx.user_email ? (
                            <div className="flex flex-col">
                              <span className="text-sm" style={{ color: "#E2E8F0" }}>{tx.user_name || tx.user_email.split("@")[0]}</span>
                              <span className="text-xs" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>{tx.user_email}</span>
                            </div>
                          ) : (
                            <span className="text-xs" style={{ color: "#64748B", fontFamily: "'IBM Plex Mono'" }}>
                              {(tx.user_id || tx.metadata?.user_id)?.slice(0, 8) || "—"}…
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 capitalize" style={{ color: "#00D4FF" }}>{tx.metadata?.type || "plan"}</td>
                        <td className="px-4 py-3 font-medium" style={{ color: "#4ADE80", fontFamily: "'IBM Plex Mono'" }}>
                          ${tx.amount?.toFixed(2)}
                        </td>
                        <td className="px-4 py-3" style={{ color: "#F0A050", fontFamily: "'IBM Plex Mono'" }}>
                          {tx.metadata?.credits || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className="text-xs rounded-full" style={{
                            background: tx.payment_status === "paid" ? "rgba(74,222,128,0.1)" : "rgba(240,160,80,0.1)",
                            color: tx.payment_status === "paid" ? "#4ADE80" : "#F0A050" }}>
                            {tx.payment_status === "paid" ? "Pagado" : "Pendiente"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pricing & Credits Config */}
        {activeTab === "pricing" && (
          <div className="space-y-6">
            {/* Credit Costs */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                  <Coins className="w-5 h-5" style={{ color: "#F0A050" }} />
                  Costo por Accion (creditos)
                </h3>
                <Button data-testid="save-credit-costs-btn" onClick={saveCreditCosts} disabled={savingCosts}
                  size="sm" className="rounded-full px-4 text-sm"
                  style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                  {savingCosts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Guardar
                </Button>
              </div>
              <p className="text-xs mb-4" style={{ color: "#94A3B8" }}>
                Define cuantos creditos cuesta cada accion. Los usuarios veran estos costos en la pagina de precios.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {Object.entries(editCosts).map(([key, cost]) => {
                  const Icon = actionIcons[key] || FileText;
                  const label = actionLabels[key] || key;
                  return (
                    <div key={key} className="rounded-lg p-4 border" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className="w-4 h-4" style={{ color: "#00D4FF" }} />
                        <span className="text-xs font-medium" style={{ color: "#E2E8F0" }}>{label}</span>
                      </div>
                      <Input data-testid={`credit-cost-${key}`} type="number" min="0" value={cost}
                        onChange={(e) => setEditCosts({ ...editCosts, [key]: parseInt(e.target.value) || 0 })}
                        className="h-10 text-center text-lg font-bold"
                        style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0", fontFamily: "'Space Grotesk'" }} />
                      <span className="text-xs block text-center mt-1" style={{ color: "#94A3B8" }}>creditos</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator style={{ background: "#1E3A5F" }} />

            {/* Plans */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                  <CreditCard className="w-5 h-5" style={{ color: "#A78BFA" }} />
                  Planes de Suscripcion
                </h3>
                <Button data-testid="save-plans-btn" onClick={savePlans} disabled={savingPlans}
                  size="sm" className="rounded-full px-4 text-sm"
                  style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                  {savingPlans ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Guardar
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(editPlans).map(([key, plan]) => (
                  <div key={key} className="rounded-xl p-5 border" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                    <div className="text-sm font-bold mb-4 capitalize" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                      {plan.name || key}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Nombre</Label>
                        <Input data-testid={`plan-name-${key}`} value={plan.name || ""}
                          onChange={(e) => setEditPlans({ ...editPlans, [key]: { ...plan, name: e.target.value } })}
                          className="h-9 text-sm"
                          style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Precio (EUR)</Label>
                        <Input data-testid={`plan-price-${key}`} type="number" step="0.01" min="0" value={plan.price || 0}
                          onChange={(e) => setEditPlans({ ...editPlans, [key]: { ...plan, price: parseFloat(e.target.value) || 0 } })}
                          className="h-9 text-sm font-bold"
                          style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#4ADE80", fontFamily: "'Space Grotesk'" }} />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Creditos incluidos</Label>
                        <Input data-testid={`plan-credits-${key}`} type="number" min="0" value={plan.credits || 0}
                          onChange={(e) => setEditPlans({ ...editPlans, [key]: { ...plan, credits: parseInt(e.target.value) || 0 } })}
                          className="h-9 text-sm font-bold"
                          style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#F0A050", fontFamily: "'Space Grotesk'" }} />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Lo que incluye</Label>
                        <Input data-testid={`plan-includes-${key}`} value={plan.includes || ""}
                          onChange={(e) => setEditPlans({ ...editPlans, [key]: { ...plan, includes: e.target.value } })}
                          placeholder="Ej: Ideal para empezar"
                          className="h-9 text-sm"
                          style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                      </div>
                      {/* Features list (bullets shown in /pricing) */}
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Características (una por línea)</Label>
                        <textarea data-testid={`plan-features-${key}`}
                          value={(Array.isArray(plan.features) ? plan.features : []).join("\n")}
                          onChange={(e) => setEditPlans({
                            ...editPlans,
                            [key]: { ...plan, features: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) }
                          })}
                          placeholder={"Artículos SEO completos\nPosts para redes sociales\nFAQ automática"}
                          rows={5}
                          className="w-full rounded-md p-2 text-sm"
                          style={{ background: "#0F2035", border: "1px solid #1E3A5F", color: "#E2E8F0", fontFamily: "'Outfit'" }} />
                      </div>
                      {/* Offer block */}
                      <div className="pt-3 mt-2" style={{ borderTop: "1px dashed #1E3A5F" }}>
                        <div className="flex items-center gap-2 mb-2 cursor-pointer"
                          onClick={() => setEditPlans({ ...editPlans, [key]: { ...plan, offer_enabled: !plan.offer_enabled } })}>
                          <div
                            data-testid={`plan-offer-toggle-${key}`}
                            className="w-4 h-4 rounded flex items-center justify-center border"
                            style={{
                              background: plan.offer_enabled ? "#4ADE80" : "transparent",
                              borderColor: plan.offer_enabled ? "#4ADE80" : "#334155",
                            }}>
                            {plan.offer_enabled && <span style={{ color: "#0B1622", fontSize: "10px", fontWeight: 900 }}>✓</span>}
                          </div>
                          <span className="text-xs" style={{ color: plan.offer_enabled ? "#4ADE80" : "#94A3B8" }}>
                            Oferta activa (precio tachado en la web)
                          </span>
                        </div>
                        {plan.offer_enabled && (
                          <>
                            <div className="mt-2">
                              <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Precio oferta (EUR)</Label>
                              <Input data-testid={`plan-offer-price-${key}`} type="number" step="0.01" min="0"
                                value={plan.offer_price || 0}
                                onChange={(e) => setEditPlans({ ...editPlans, [key]: { ...plan, offer_price: parseFloat(e.target.value) || 0 } })}
                                className="h-9 text-sm font-bold"
                                style={{ background: "#0F2035", borderColor: "#4ADE80", color: "#4ADE80", fontFamily: "'Space Grotesk'" }} />
                            </div>
                            <div className="mt-2">
                              <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Texto de oferta</Label>
                              <Input data-testid={`plan-offer-text-${key}`} value={plan.offer_text || ""}
                                onChange={(e) => setEditPlans({ ...editPlans, [key]: { ...plan, offer_text: e.target.value } })}
                                placeholder="Ej: Oferta de lanzamiento"
                                className="h-9 text-sm"
                                style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator style={{ background: "#1E3A5F" }} />

            {/* Initial Credits */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    Creditos gratuitos al registrarse
                  </h3>
                  <p className="text-xs mt-1" style={{ color: "#94A3B8" }}>
                    Cuantos creditos recibe cada usuario nuevo al crear su cuenta.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid="initial-credits-input" type="number" min="0" value={initialCredits}
                    onChange={(e) => setInitialCredits(parseInt(e.target.value) || 0)}
                    className="w-20 h-9 text-center text-sm font-bold"
                    style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#4ADE80", fontFamily: "'Space Grotesk'" }} />
                  <Button data-testid="save-initial-credits-btn" onClick={saveInitialCredits} disabled={savingInitialCredits}
                    size="sm" className="rounded-full px-3 h-9"
                    style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                    {savingInitialCredits ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <Separator style={{ background: "#1E3A5F" }} />

            {/* Credit Packs */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                  <Coins className="w-5 h-5" style={{ color: "#4ADE80" }} />
                  Packs de Creditos Sueltos
                </h3>
                <Button data-testid="save-packs-btn" onClick={savePacks} disabled={savingPacks}
                  size="sm" className="rounded-full px-4 text-sm"
                  style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                  {savingPacks ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Guardar
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {editPacks.map((pack, idx) => (
                  <div key={pack.id} className="rounded-xl p-5 border" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                    <div className="text-sm font-bold mb-3 capitalize" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                      Pack {pack.credits} creditos
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Creditos</Label>
                        <Input data-testid={`pack-credits-${idx}`} type="number" min="1" value={pack.credits}
                          onChange={(e) => updatePack(idx, "credits", parseInt(e.target.value) || 0)}
                          className="h-9 text-sm font-bold"
                          style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#F0A050", fontFamily: "'Space Grotesk'" }} />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "#94A3B8" }}>Precio (EUR)</Label>
                        <Input data-testid={`pack-price-${idx}`} type="number" step="0.01" min="0" value={pack.price}
                          onChange={(e) => updatePack(idx, "price", parseFloat(e.target.value) || 0)}
                          className="h-9 text-sm font-bold"
                          style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#4ADE80", fontFamily: "'Space Grotesk'" }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tiers */}
        {activeTab === "tiers" && (
          <div className="space-y-6">
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <Shield className="w-5 h-5" style={{ color: "#FBBF24" }} />
                    Configuracion de Tiers por Plan
                  </h3>
                  <p className="text-xs mt-1" style={{ color: "#94A3B8" }}>
                    Selecciona que planes tienen acceso a cada funcionalidad. Los usuarios sin acceso veran un candado con opcion de upgrade.
                  </p>
                </div>
                <Button data-testid="save-tiers-btn" onClick={saveTierConfig} disabled={savingTiers}
                  className="rounded-full px-5 text-sm"
                  style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                  {savingTiers ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Guardar
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: "1px solid #1E3A5F" }}>
                      <th className="text-left text-xs py-3 pr-4" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>Funcionalidad</th>
                      {["free", "basic", "pro", "business"].map((plan) => (
                        <th key={plan} className="text-center text-xs py-3 px-4" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>
                          {plan.charAt(0).toUpperCase() + plan.slice(1)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "voice_profile", label: "Perfil de Voz", desc: "5 dimensiones + tonos expresivos + analisis IA" },
                      { key: "editorial_angle", label: "Angulo Editorial", desc: "8 angulos y 8 objetivos de contenido" },
                      { key: "content_objective", label: "Objetivo del Contenido", desc: "Configurar objetivo para cada articulo" },
                      { key: "multi_language", label: "Multi-idioma", desc: "Generar contenido en 6 idiomas" },
                    ].map((feature) => (
                      <tr key={feature.key} style={{ borderBottom: "1px solid #1E3A5F22" }}>
                        <td className="py-3 pr-4">
                          <div className="text-sm font-medium" style={{ color: "#E2E8F0" }}>{feature.label}</div>
                          <div className="text-xs" style={{ color: "#64748B" }}>{feature.desc}</div>
                        </td>
                        {["free", "basic", "pro", "business"].map((plan) => {
                          const isEnabled = (tierConfig[feature.key] || []).includes(plan);
                          return (
                            <td key={plan} className="text-center py-3 px-4">
                              <button data-testid={`tier-${feature.key}-${plan}`}
                                onClick={() => toggleTierPlan(feature.key, plan)}
                                className="w-8 h-8 rounded-lg border transition-all duration-200 flex items-center justify-center mx-auto"
                                style={{
                                  background: isEnabled ? "rgba(74,222,128,0.1)" : "rgba(148,163,184,0.05)",
                                  borderColor: isEnabled ? "rgba(74,222,128,0.3)" : "#1E3A5F",
                                }}>
                                {isEnabled ? (
                                  <svg className="w-4 h-4" style={{ color: "#4ADE80" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <span className="w-4 h-0.5 rounded" style={{ background: "#475569" }} />
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 rounded-lg p-3 border" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                <p className="text-xs" style={{ color: "#64748B" }}>
                  Los administradores siempre tienen acceso a todas las funcionalidades independientemente de su plan.
                  Los cambios se aplican inmediatamente a todos los usuarios.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Settings */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            {/* Stripe Config */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3 className="text-base font-semibold flex items-center gap-2 mb-4" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                <Key className="w-5 h-5" style={{ color: "#A78BFA" }} />
                Configuracion de Stripe
              </h3>
              <p className="text-xs mb-4" style={{ color: "#94A3B8" }}>
                {stripeStatus?.mode === "live"
                  ? "Stripe esta configurado en modo LIVE. Los pagos son reales."
                  : "Para recibir pagos reales, necesitas configurar tu clave de Stripe (sk_live_... o rk_live_...)."}
              </p>
              <div className="rounded-lg p-4 border" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: stripeStatus?.mode === "live" ? "rgba(74,222,128,0.1)" : "rgba(167,139,250,0.1)" }}>
                    <CreditCard className="w-5 h-5" style={{ color: stripeStatus?.mode === "live" ? "#4ADE80" : "#A78BFA" }} />
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: "#E2E8F0" }}>Stripe API</div>
                    <div className="text-xs" style={{ color: "#94A3B8" }}>
                      Estado: <span style={{ color: stripeStatus?.mode === "live" ? "#4ADE80" : "#FBBF24" }}>
                        {stripeStatus?.mode === "live" ? "Modo Live" : "Modo Test"}
                      </span>
                      {stripeStatus?.key_prefix && (
                        <span className="ml-2 font-mono" style={{ color: "#64748B" }}>({stripeStatus.key_prefix})</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* WordPress Config */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3 className="text-base font-semibold flex items-center gap-2 mb-4" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                <Settings className="w-5 h-5" style={{ color: "#4ADE80" }} />
                WordPress
              </h3>
              <div className="rounded-lg p-4 border" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(74,222,128,0.1)" }}>
                    <Settings className="w-5 h-5" style={{ color: "#4ADE80" }} />
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: "#E2E8F0" }}>WordPress REST API</div>
                    <div className="text-xs" style={{ color: "#4ADE80" }}>Conectado</div>
                  </div>
                </div>
                <p className="text-xs mt-3" style={{ color: "#94A3B8" }}>
                  Las credenciales de WordPress estan configuradas en el servidor (WP_URL, WP_USERNAME, WP_APP_PASSWORD).
                </p>
              </div>
            </div>

            {/* Stripe — activación en producción */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3 className="text-base font-semibold flex items-center gap-2 mb-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                <CreditCard className="w-5 h-5" style={{ color: "#00D4FF" }} />
                Stripe · Productos y precios
                {stripeStatus && (
                  <span className="text-xs px-2 py-0.5 rounded-full ml-2"
                    style={{
                      background: stripeStatus.mode === "live" ? "rgba(74,222,128,0.1)" : "rgba(251,191,36,0.1)",
                      color: stripeStatus.mode === "live" ? "#4ADE80" : "#FBBF24",
                      border: `1px solid ${stripeStatus.mode === "live" ? "rgba(74,222,128,0.3)" : "rgba(251,191,36,0.3)"}`,
                    }}>
                    {stripeStatus.mode === "live" ? "LIVE" : stripeStatus.mode}
                  </span>
                )}
                {stripeStatus && (
                  <span className="text-xs px-2 py-0.5 rounded-full"
                    style={{
                      background: stripeStatus.webhook_configured ? "rgba(74,222,128,0.1)" : "rgba(239,68,68,0.1)",
                      color: stripeStatus.webhook_configured ? "#4ADE80" : "#f87171",
                    }}>
                    Webhook {stripeStatus.webhook_configured ? "configurado" : "PENDIENTE"}
                  </span>
                )}
              </h3>
              <p className="text-xs mb-3" style={{ color: "#94A3B8", lineHeight: 1.5 }}>
                Sincroniza los 4 planes actuales como productos/precios recurrentes en Stripe. Es idempotente (no crea duplicados).
                Usa el <code style={{ color: "#00D4FF" }}>offer_price</code> si la oferta está activa, si no el <code style={{ color: "#00D4FF" }}>price</code> normal.
              </p>
              <Button
                data-testid="stripe-sync-btn"
                onClick={syncStripeProducts}
                disabled={syncingStripe}
                className="text-xs"
                style={{ background: "linear-gradient(135deg, #635BFF, #7A73FF)", color: "white" }}>
                {syncingStripe ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />}
                Sincronizar con Stripe
              </Button>
              {stripeSyncResult && (
                <div className="mt-3 text-xs p-3 rounded" style={{ background: "#0B1622", border: "1px solid #1E3A5F" }}>
                  {stripeSyncResult.products?.map((p) => (
                    <div key={p.plan_id} className="flex items-center justify-between py-1">
                      <span style={{ color: "#E2E8F0" }}>{p.name}</span>
                      <span style={{ color: "#94A3B8", fontFamily: "monospace" }}>{p.price_id}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs" style={{
                        background: p.status === "created" ? "rgba(74,222,128,0.1)" : "rgba(148,163,184,0.1)",
                        color: p.status === "created" ? "#4ADE80" : "#94A3B8",
                      }}>{p.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pipedream workflow URLs — Roadmap */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F", opacity: 0.85 }}>
              <h3 className="text-base font-semibold flex items-center gap-2 mb-2" style={{ color: "#94A3B8", fontFamily: "'Space Grotesk'" }}>
                <Share2 className="w-5 h-5" style={{ color: "#64748B" }} />
                Distribucion automatica a redes
                <span className="text-xs px-2 py-0.5 rounded-full ml-2" style={{ background: "rgba(148,163,184,0.15)", color: "#94A3B8", border: "1px solid rgba(148,163,184,0.3)" }}>
                  Roadmap · Plan Business
                </span>
              </h3>
              <p className="text-xs" style={{ color: "#64748B", lineHeight: 1.5 }}>
                Configuración de workflows Pipedream. Funcionalidad disponible para clientes del plan Business una vez aprobadas las APIs de Meta y LinkedIn. Esta sección solo la ve el admin.
              </p>
            </div>
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3 className="text-base font-semibold flex items-center gap-2 mb-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                <Share2 className="w-5 h-5" style={{ color: "#00D4FF" }} />
                Distribucion automatica a redes
                <span className="text-xs px-2 py-0.5 rounded-full ml-2" style={{ background: "rgba(74,222,128,0.1)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>
                  Pipedream
                </span>
              </h3>
              <p className="text-xs mb-4" style={{ color: "#94A3B8", lineHeight: 1.6 }}>
                URL del webhook/workflow de Pipedream para cada red. Cuando el cliente pulse <strong style={{ color: "#E2E8F0" }}>"Publicar en redes"</strong>,
                el backend invocara el workflow correspondiente pasando el <code style={{ color: "#00D4FF" }}>account_id</code> del cliente.
                Solo visible para admin.
              </p>
              <div className="space-y-2">
                {[
                  { id: "linkedin", label: "LinkedIn" },
                  { id: "x", label: "X (Twitter)" },
                  { id: "facebook", label: "Facebook Pages" },
                  { id: "instagram", label: "Instagram Business" },
                ].map((n) => (
                  <div key={n.id} className="flex items-center gap-2">
                    <span className="text-xs w-36" style={{ color: "#94A3B8" }}>{n.label}</span>
                    <Input
                      data-testid={`pd-workflow-${n.id}`}
                      value={pdWorkflows[n.id] || ""}
                      onChange={(e) => setPdWorkflows({ ...pdWorkflows, [n.id]: e.target.value })}
                      placeholder={`https://xxx.m.pipedream.net/ (workflow ${n.label})`}
                      className="h-9 text-xs font-mono flex-1"
                      style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                    />
                  </div>
                ))}
              </div>
              <Button
                data-testid="admin-save-pd-workflows"
                onClick={savePdWorkflows}
                disabled={savingPdWorkflows}
                className="text-xs mt-3"
                style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}
              >
                {savingPdWorkflows ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Guardar workflows
              </Button>
              <div className="mt-3 text-xs p-2.5 rounded" style={{ background: "rgba(0,212,255,0.04)", color: "#94A3B8", border: "1px dashed rgba(0,212,255,0.2)" }}>
                <strong style={{ color: "#00D4FF" }}>Setup:</strong> Crea un workflow en Pipedream por red, con trigger HTTP, que reciba <code>{`{account_id, content, article}`}</code> y publique usando el account del usuario.
                Pega aquí la URL del trigger HTTP de cada uno.
              </div>
            </div>

            {/* Info */}
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3 className="text-base font-semibold flex items-center gap-2 mb-4" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                <Shield className="w-5 h-5" style={{ color: "#00D4FF" }} />
                Acceso Admin
              </h3>
              <p className="text-xs" style={{ color: "#94A3B8", lineHeight: 1.7 }}>
                El primer usuario registrado se convierte automaticamente en administrador.
                Puedes dar o quitar acceso admin a otros usuarios desde la pestana "Usuarios".
                Solo los administradores pueden ver este panel.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
